import { ArrowUpRight, Linkedin } from 'lucide-react';
import { useContext, useId } from 'react';
import { useIntl } from 'react-intl';
import { route } from 'ziggy-js';
import Container from '@/Components/Marketing/Container';
import { FOCUS_RING, RING_SURFACE } from '@/Components/Marketing/focusRing';
import { CONSULTATION_URL, currentPathname, isCurrent, livePassportUrl } from '@/Components/Marketing/links';
import Logo from '@/Components/Marketing/Logo';
import NewsletterForm from '@/Components/Marketing/NewsletterForm';
import SmartLink, { hrefKind } from '@/Components/Marketing/SmartLink';
import { LanguageContext } from '@/lib/LanguageProviders';
import { cn } from '@/lib/utils';

const LANGUAGES = [
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' },
    { code: 'bg', label: 'BG' },
];

// A page that has no Spanish or Bulgarian copy yet offers English alone
// (ask 6): ES and BG would change only a few words inside it. English stays
// a button, so a visitor who picked another language on the calendar can
// come back from any page.
const ENGLISH_ONLY = LANGUAGES.slice(0, 1);

const LINKEDIN_URL = 'https://www.linkedin.com/company/synex-cloud';

// It names its focus rings' offset (focusRing.js): on the sunken footer the
// ring is two-tone (orange alone is 2.91:1 there, audit 16).
const SURFACE = `bg-mk-sunken text-mk-ink ${RING_SURFACE.sunken}`;

const DIVIDER = 'border-t border-mk-line';

/**
 * The marketing footer: logo and tagline, three link columns, the newsletter,
 * then copyright, the status badge and the language switcher, under a top
 * hairline. `newsletter={false}` leaves the sign-up out, on a page that
 * carries its own (Resources). `translated` offers every language, on a page
 * translated into them (the calendar); any other offers English alone.
 */
export default function Footer({ newsletter = true, translated = false, className }) {
    const intl = useIntl();
    const t = (id, values) => intl.formatMessage({ id }, values);
    // The switcher drives the app-wide LanguageProvider; outside one (a test,
    // a stray render) it shows the intl locale and does nothing on press.
    const { locale = intl.locale, setLocale } = useContext(LanguageContext) ?? {};
    const currentPath = currentPathname();

    // The battery passport explainer sits with Consulting, whose pages it is
    // part of (/consulting/battery-passport).
    const columns = [
        {
            key: 'platform',
            heading: t('site.footer.product'),
            links: [
                { label: t('site.nav.platform'), href: route('outer.platform') },
                { label: t('site.cta.livePassport'), href: livePassportUrl() },
            ],
        },
        {
            key: 'consulting',
            heading: t('site.footer.compliance'),
            links: [
                { label: t('site.nav.consulting'), href: route('outer.consulting') },
                { label: t('site.nav.batteryPassport'), href: route('outer.batterypassport') },
                { label: t('site.nav.calendar'), href: route('outer.batterycompliancecalendar') },
                { label: t('site.cta.consultation'), href: CONSULTATION_URL },
            ],
        },
        {
            key: 'company',
            heading: t('site.footer.company'),
            links: [
                { label: t('site.nav.partners'), href: route('outer.partnerships') },
                { label: t('site.nav.guides'), href: route('outer.resources') },
                { label: t('site.footer.contact'), href: route('outer.contact') },
            ],
        },
    ];

    return (
        <footer className={cn(SURFACE, DIVIDER, className)}>
            <Container className="pb-10 pt-16 sm:pt-20">
                <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-8">
                    <div className="lg:col-span-4">
                        <SmartLink
                            href={route('outer.welcome')}
                            aria-label={t('site.nav.home')}
                            className={cn('-mx-1 inline-flex rounded-md px-1 py-1', FOCUS_RING)}
                        >
                            <Logo className="h-7" />
                        </SmartLink>
                        <p className="mt-4 max-w-xs text-sm leading-relaxed text-mk-body">
                            {t('site.footer.tagline')}
                        </p>
                        <SmartLink
                            href={LINKEDIN_URL}
                            className={cn(
                                '-mx-1.5 mt-6 inline-flex size-9 items-center justify-center rounded-md text-mk-subtle transition-colors duration-300 ease-mk hover:text-mk-ink',
                                FOCUS_RING,
                            )}
                        >
                            <Linkedin aria-hidden="true" strokeWidth={1.5} className="size-5" />
                            <span className="sr-only">{t('site.footer.linkedin')}</span>
                        </SmartLink>
                    </div>

                    <nav aria-label={t('site.footer.nav')} className="grid grid-cols-1 gap-10 md:grid-cols-3 md:gap-8 lg:col-span-8">
                        {columns.map(column => (
                            <div key={column.key}>
                                <h2 className="text-sm font-medium text-mk-ink">{column.heading}</h2>
                                <ul role="list" className="mt-5 space-y-3">
                                    {column.links.map(link => (
                                        <li key={link.href}>
                                            <FooterLink link={link} currentPath={currentPath} />
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>
                </div>

                {newsletter && (
                    <div className={cn('mt-16 pt-10 sm:mt-20', DIVIDER)}>
                        <Newsletter t={t} />
                    </div>
                )}

                <div
                    className={cn(
                        'mt-10 flex flex-col gap-6 pt-8 md:flex-row md:items-center md:justify-between',
                        DIVIDER,
                    )}
                >
                    <p className="text-sm text-mk-subtle">
                        {t('site.footer.copyright', { year: new Date().getFullYear() })}
                    </p>
                    {/* Out of Sentry's session replay (data-sentry-block): its
                        recorder reached into this cross-origin frame on load,
                        which WebKit reports as an error on every page (audit 4). */}
                    <iframe
                        data-sentry-block=""
                        title={t('site.footer.status')}
                        src="https://status.synexcloud.com/badge?theme=light"
                        width="250"
                        height="30"
                        loading="lazy"
                        scrolling="no"
                        className="block max-w-full border-0"
                        style={{ colorScheme: 'normal' }}
                    />
                    <div role="group" aria-label={t('site.footer.language')} className="flex items-center gap-1">
                        {(translated ? LANGUAGES : ENGLISH_ONLY).map((language, index) => (
                            <span key={language.code} className="flex items-center">
                                {index > 0 && (
                                    <span aria-hidden="true" className="mx-1 select-none text-mk-line">
                                        |
                                    </span>
                                )}
                                <LanguageButton
                                    language={language}
                                    active={locale === language.code}
                                    onSelect={() => setLocale?.(language.code)}
                                />
                            </span>
                        ))}
                    </div>
                </div>
            </Container>
        </footer>
    );
}

function LanguageButton({ language, active, onSelect }) {
    return (
        <button
            type="button"
            lang={language.code}
            aria-pressed={active}
            onClick={onSelect}
            className={cn(
                'rounded-md px-1.5 py-1 text-sm tracking-wide transition-colors duration-300 ease-mk',
                active ? 'font-semibold text-mk-ink' : 'text-mk-subtle hover:text-mk-ink',
                FOCUS_RING,
            )}
        >
            {language.label}
        </button>
    );
}

/** A footer link; one that leaves the site says so (SmartLink) and shows an arrow. */
function FooterLink({ link, currentPath }) {
    const leaves = hrefKind(link.href) === 'external';
    const current = !leaves && isCurrent(link.href, currentPath);

    return (
        <SmartLink
            href={link.href}
            aria-current={current ? 'page' : undefined}
            className={cn(
                'group -mx-1 inline-flex items-center gap-1 rounded-md px-1 text-sm transition-colors duration-300 ease-mk',
                current ? 'font-medium text-mk-ink' : 'text-mk-body hover:text-mk-ink',
                FOCUS_RING,
            )}
        >
            {link.label}
            {leaves && (
                <ArrowUpRight
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className="size-3.5 transition-transform duration-300 ease-mk motion-safe:fine:group-hover:-translate-y-px motion-safe:fine:group-hover:translate-x-px"
                />
            )}
        </SmartLink>
    );
}

/**
 * The newsletter sign-up: its heading and one line beside the shared form
 * (NewsletterForm, which the Resources page's #newsletter section uses too).
 * The footer's copy sends no analytics event.
 */
function Newsletter({ t }) {
    const headingId = useId();

    return (
        <section aria-labelledby={headingId} className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-8">
            <div className="lg:col-span-4">
                <h2 id={headingId} className="text-sm font-medium text-mk-ink">
                    {t('site.footer.newsletter.title')}
                </h2>
                <p className="mt-2 max-w-xs text-sm leading-relaxed text-mk-body">
                    {t('site.footer.newsletter.text')}
                </p>
            </div>

            <NewsletterForm className="lg:col-span-6 lg:col-start-5" />
        </section>
    );
}
